import React, { useEffect } from "react";
import Layout from "../components/ui/Layout";
import { motion } from "framer-motion";
import { useAgentDock } from "../context/AgentDockContext";
import { WRITINGS } from "../data/writings";
import SocialLinks from "../components/ui/SocialLinks";

const AboutMe = () => {
  const { openDock } = useAgentDock();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <Layout>
      {/* About Me Content */}
      <section className="pt-12 pb-20 sm:pt-16 sm:pb-24 md:pt-20 md:pb-32 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
          <motion.div
            className="flex flex-col md:flex-row gap-12 items-start"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              className="w-full md:w-2/3"
              variants={slideInLeft}
              transition={{ duration: 0.8 }}
            >
              <motion.div className="about-photo-wrap w-full overflow-hidden rounded-lg" animate={floatAnimation}>
                <img
                  src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1740996122/Frame_1000004303_zm4cyu.png"
                  alt="Young playing with a cat"
                  className="about-photo h-auto max-h-[800px]"
                />
              </motion.div>
              <SocialLinks className="mt-4" />
            </motion.div>

            {/* Self Intro Section */}
            <motion.div
              className="w-full md:w-1/3 space-y-8"
              variants={slideInRight}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-lg leading-relaxed text-gray-300">
                Designing product since 2022, with an academic background in Human Development, Learning, and Culture (Master's) and English Literature (Bachelor's).
              </p>
              <p className="text-lg leading-relaxed text-gray-300">
                Outside of work version of me: Rednote content creator on UX, and mom of a kitten.
              </p>
              <motion.button
                className="group bg-primary text-white px-6 py-3 flex items-center gap-3 border-0 rounded shadow-lg transition-all duration-300 ease-in-out hover:shadow-xl"
                onClick={() =>
                  window.open(
                    "https://intranetconnections.com/blog/intranet-3-0-credit-union-needs/",
                    "_blank"
                  )
                }
                aria-label="Read My Story"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                Read My Blog Story at IC

                {/* Default Arrow (hidden on hover) */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 block group-hover:hidden"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>

                {/* GIF (shown on hover) */}
                <img
                  src="https://res.cloudinary.com/dlzvz9qj8/image/upload/v1745999991/Animation_-_1745999791954_qebdj3.gif"
                  alt="Eyes animation"
                  className="w-6 h-6 hidden group-hover:block"
                />
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Career Path and Certificates Section */}
      <section className="pt-8 pb-12 sm:pt-10 sm:pb-12 md:pt-12 md:pb-16 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
          <motion.div
            className="flex flex-col md:flex-row gap-12 items-start"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {/* Career Path */}
            <motion.div
              className="w-full max-w-lg shrink-0 space-y-6"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-light text-white">
                My career path: We will finally land where we belong.
              </h2>
              <div className="space-y-4 text-base leading-relaxed text-gray-300">
                <p>
                  My path to product design wasn't a straight line. In my early 20s, I followed my curiosity and bounced onto different roles to English teacher, translator, digital marketer, content editor, and admissions coordinator. Each experience gave me a new lens. Eventually, that winding journey led me to product design where I found peaceful joy untangling complex problems through wireframes and crafting accessible, thoughtful experiences. It finally felt like home.
                </p>
                <p>
                  I taught myself through courses, books, and practice, and I'm grateful to be part of a community striving to improve human experiences. I'll keep doing so.
                </p>
              </div>
            </motion.div>

            {/* Certificates */}
            <motion.div
              className="w-full min-w-0 flex-1"
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-2xl md:text-3xl font-light text-white mb-8">
                Certificates
              </h2>
              <motion.div
                className="grid grid-cols-2 gap-4 md:gap-6"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={staggerContainer}
              >
                <motion.div
                  variants={fadeIn}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="https://s3.amazonaws.com/coursera_assets/meta_images/generated/CERTIFICATE_LANDING_PAGE/CERTIFICATE_LANDING_PAGE~BR3E8R8SJWGA/CERTIFICATE_LANDING_PAGE~BR3E8R8SJWGA.jpeg"
                    alt="JBTD Badge"
                    className="w-full aspect-square object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                  />
                </motion.div>
                <motion.div
                  variants={fadeIn}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="https://images.credly.com/size/680x680/images/9db377a2-0dd1-4fcd-a531-5f7a9b920759/image.png"
                    alt="JBTD Badge- Fundamental"
                    className="w-full aspect-square object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                  />
                </motion.div>
                <motion.div
                  variants={fadeIn}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="https://s3.amazonaws.com/coursera_assets/meta_images/generated/CERTIFICATE_LANDING_PAGE/CERTIFICATE_LANDING_PAGE~FYEY7PS5KYNV/CERTIFICATE_LANDING_PAGE~FYEY7PS5KYNV.jpeg"
                    alt="Coding for Managers"
                    className="w-full aspect-square object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                  />
                </motion.div>
                <motion.div
                  variants={fadeIn}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="https://api.badgr.io/public/assertions/sZOkaxB6QzOjNHrBUoqrrg/image"
                    alt="AI in Action"
                    className="w-full aspect-square object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                  />
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-20 bg-black text-white" aria-labelledby="about-writing">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
          >
            <motion.h2
              id="about-writing"
              className="text-2xl md:text-3xl font-light text-white mb-10"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              Writing
            </motion.h2>
            <div className="max-w-3xl space-y-12">
              {WRITINGS.map((article) => (
                <motion.article key={article.href} variants={fadeInUp} transition={{ duration: 0.5 }}>
                  <p className="text-white/40 text-xs mb-2">
                    {article.date} · {article.tags}
                  </p>
                  <h3 className="text-xl md:text-2xl font-light leading-tight mb-2">{article.title}</h3>
                  <p className="text-gray-300 text-base leading-relaxed max-w-xl mb-4">{article.blurb}</p>
                  <a
                    href={article.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-white/80 hover:text-white underline underline-offset-4"
                  >
                    Read on Medium
                  </a>
                </motion.article>
              ))}
            </div>

            <motion.div
              className="mt-16 pt-12 border-t border-white/10 max-w-xl space-y-4"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-light text-white">Want to know more?</h2>
              <p className="text-base leading-relaxed text-gray-300">
                Chat with Digital Young to discover more about my work, process, and anything that isn’t on this page.
              </p>
              <button
                type="button"
                onClick={openDock}
                className="bg-white text-black px-6 py-3 text-sm font-medium hover:bg-white/90 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
              >
                Chat with Digital Young
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </Layout>
  );
};

export default AboutMe;
